import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { PROJECTS } from "../data";
import { useLang, STR } from "../i18n";

const reveal = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-100px" },
  transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] },
};

export const Portfolio = () => {
  const [selected, setSelected] = useState(null);
  const { lang } = useLang();
  const t = STR[lang].portfolio;

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setSelected(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <section id="progetti" className="bg-white pt-[72px] pb-[80px]" data-testid="portfolio-section">
      <div className="max-w-[1320px] mx-auto px-6">
        <motion.div {...reveal} className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-[25px]">
          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] text-[#F18A00] font-bold" data-testid="portfolio-eyebrow">
              {t.eyebrow}
            </p>
            <h2 className="font-display font-medium text-[30px] tracking-[-0.02em] text-[#0D1B2A] mt-[7px]" data-testid="portfolio-title">
              {t.title}
            </h2>
            <span className="block w-[38px] h-0.5 bg-[#F18A00] mt-[14px]" aria-hidden="true" />
          </div>
          <p className="text-xs text-[#69747c] md:text-right" data-testid="portfolio-intro">
            {t.intro}
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-[13px]" data-testid="portfolio-grid">
          {PROJECTS.map((p, i) => (
            <motion.article
              key={p.name}
              {...reveal}
              transition={{ ...reveal.transition, delay: (i % 4) * 0.07 }}
              className="group border border-[#e3e7ea] bg-white shadow-[0_8px_25px_rgba(13,27,42,0.05)] cursor-pointer"
              onClick={() => setSelected(p)}
              data-testid={`project-card-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            >
              <div className="overflow-hidden">
                <img
                  src={p.image}
                  alt={p.name}
                  loading="lazy"
                  className="w-full h-[175px] object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                />
              </div>
              <div className="px-[14px] pt-[14px] pb-[18px]">
                <small className="text-[10px] uppercase font-bold text-[#0D1B2A]">{t.cats[p.cat] || p.cat}</small>
                <h3 className="text-[13px] uppercase font-medium text-[#0D1B2A] mt-[7px] mb-[6px] leading-snug">{p.name}</h3>
                <p className="text-[11px] text-[#68737b] m-0">{lang === "it" ? p.type : p.typeEn}</p>
                <p className="text-[11px] text-[#68737b] m-0 mt-1">
                  {lang === "it" ? (
                    <>Progettazione <span className="text-[#F18A00]">•</span> Direzione lavori</>
                  ) : (
                    <>Design <span className="text-[#F18A00]">•</span> Construction management</>
                  )}
                </p>
              </div>
            </motion.article>
          ))}
        </div>

        <div className="text-center mt-[22px] tracking-[8px] text-[#c5ccd1] text-[10px]" aria-hidden="true" data-testid="portfolio-dots">
          <b className="text-[#F18A00]">●</b> ● ● ●
        </div>
      </div>

      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[70] bg-[#0D1B2A]/95 flex items-center justify-center p-6 cursor-zoom-out"
            onClick={() => setSelected(null)}
            data-testid="project-lightbox"
          >
            <button
              className="absolute top-6 right-6 text-white/70 hover:text-[#F18A00] transition-colors"
              onClick={() => setSelected(null)}
              aria-label="Chiudi"
              data-testid="project-lightbox-close"
            >
              <X size={32} strokeWidth={1.25} />
            </button>
            <motion.figure
              initial={{ scale: 0.94, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.94, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              className="max-w-[92vw]"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={selected.image}
                alt={selected.name}
                className="max-h-[82vh] max-w-full object-contain"
                data-testid="project-lightbox-image"
              />
              <figcaption className="mt-4 text-center">
                <span className="text-[10px] uppercase tracking-[0.12em] text-[#F18A00] font-bold">{t.cats[selected.cat] || selected.cat}</span>
                <p className="text-white text-sm uppercase tracking-wide mt-1" data-testid="project-lightbox-caption">{selected.name}</p>
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
